import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Result } from '../models/result.js';
import { getAllSubjects } from '../repositories/school/subjectRepo.js';
import { getLessonPlanPerformanceReport } from '../repositories/school/lessonPlanRepo.js';

const useLessonPlanStore = create(
    persist(
        (set, get) => ({
            // Subjects State
            subjects: [],
            isSubjectsLoading: false,
            subjectsError: null,

            // Filter Selections
            selectedClass: '',
            selectedSubject: '',
            selectedSubjectId: null,
            selectedChapterId: null,

            // Report State
            report: null,
            isReportLoading: false,
            reportError: null,

            /**
             * Fetches all subjects for the school and specific class
             * @param {string} schoolId 
             * @param {string|number} classId
             */
            fetchSubjects: async (schoolId, classId) => {
                if (!schoolId || classId === undefined || classId === null || String(classId).trim() === '') {
                    set({ subjects: [], isSubjectsLoading: false });
                    return new Result({ message: 'School ID and Class ID are required' });
                }

                set({ isSubjectsLoading: true, subjectsError: null });
                const result = await getAllSubjects(schoolId, classId);

                if (result.success) {
                    set({ subjects: result.value || [], isSubjectsLoading: false });
                } else {
                    set({ subjects: [], subjectsError: result.message, isSubjectsLoading: false });
                }
                return result;
            },

            /**
             * Sets selected class, refetches subjects for that class, and resets subject selection
             * @param {string|number} classId 
             * @param {string} [schoolId]
             */
            setSelectedClass: (classId, schoolId) => {
                const prevClass = get().selectedClass;
                const isClassChanged = String(prevClass) !== String(classId);

                if (isClassChanged) {
                    set({ 
                        selectedClass: classId, 
                        selectedSubject: '', 
                        selectedSubjectId: null, 
                        selectedChapterId: null,
                        report: null,
                        subjects: []
                    });

                    if (classId && schoolId) {
                        get().fetchSubjects(schoolId, classId);
                    }
                } else {
                    set({ selectedClass: classId });
                    if (classId && schoolId && get().subjects.length === 0) {
                        get().fetchSubjects(schoolId, classId);
                    }
                }
            },

            /**
             * Sets selected subject and optionally triggers report if class is already selected
             * @param {string} subject 
             * @param {string|number} [subjectId] 
             * @param {string} [schoolId]
             */
            setSelectedSubject: (subject, subjectId = null, schoolId = null) => {
                set({ selectedSubject: subject, selectedSubjectId: subjectId, selectedChapterId: null });

                const { selectedClass } = get();
                if (selectedClass && (subject || subjectId) && schoolId) {
                    get().fetchLessonPlanReport({
                        schoolId,
                        classId: selectedClass,
                        subject,
                        subjectId
                    });
                }
            },

            /**
             * Select a specific chapter/lesson plan for focused inspection in desktop UI
             * @param {number|string|null} chapterId 
             */
            setSelectedChapterId: (chapterId) => {
                set({ selectedChapterId: chapterId });
            },

            /**
             * Queries the Lesson Plan Report API
             * @param {Object} params - { schoolId, classId, subject, subjectId, teacherId }
             */
            fetchLessonPlanReport: async (params = {}) => {
                const { schoolId, classId, subject, subjectId, teacherId } = params;

                if (!schoolId || !classId || (!subject && !subjectId)) {
                    return new Result({ message: "schoolId, classId, and subject are required" });
                }

                set({ isReportLoading: true, reportError: null });

                const result = await getLessonPlanPerformanceReport({
                    schoolId,
                    classId,
                    subject,
                    subjectId,
                    teacherId
                });

                if (result.success) {
                    const reportData = result.value;
                    const firstChapterId = reportData?.lessonPlans?.length > 0 
                        ? reportData.lessonPlans[0].lessonPlanId 
                        : null;

                    set({ 
                        report: reportData, 
                        isReportLoading: false, 
                        reportError: null,
                        selectedChapterId: firstChapterId
                    });
                } else {
                    set({ 
                        report: null, 
                        isReportLoading: false, 
                        reportError: result.message || 'Failed to fetch lesson plan performance report' 
                    });
                }

                return result;
            },

            /**
             * Clears current report and selections
             */
            clearReport: () => {
                set({
                    report: null,
                    reportError: null,
                    isReportLoading: false,
                    selectedClass: '',
                    selectedSubject: '',
                    selectedSubjectId: null,
                    selectedChapterId: null
                });
            }
        }),
        {
            name: 'lesson-plan-storage',
            partialize: (state) => ({
                selectedClass: state.selectedClass,
                selectedSubject: state.selectedSubject,
                selectedSubjectId: state.selectedSubjectId
            })
        }
    )
);

export default useLessonPlanStore;
